import React from "react";
import PolicyLayout, { Bullets } from "@/components/PolicyLayout";
import { Mail, Globe, MapPin, Clock, LifeBuoy } from "lucide-react";
import { useT } from "@/lib/content";

export default function ContactUs() {
  const company = useT("contact.company_name", "Teuralink");
  const tagline = useT("contact.tagline", "Premium Commemorative Moon & Mars Gift Registry");
  const address = useT("contact.address",
    "Haripur\nP.O. Illashpur\nP.S. Bhagwanpur\nPurba Medinipur (East Medinipur)\nWest Bengal – 721601\nIndia");
  const website = useT("contact.website", "www.teuralink.com");
  const email = useT("contact.email", "support@teuralink.com");
  const hours = useT("contact.hours", "Monday – Saturday\n10:00 AM – 7:00 PM (Indian Standard Time)");
  const responseTime = useT("contact.response_time",
    "We aim to respond to all customer inquiries within 24 to 48 business hours.");

  return (
    <PolicyLayout title="Contact Us" tagline="Support · Get in Touch" updated="Feb 2026">
      <p>
        At Teuralink, we are committed to providing exceptional customer service. Whether you have questions
        about your order, commemorative registry, gift packages, shipping, or general inquiries, our support
        team is here to assist you.
      </p>

      <div className="grid md:grid-cols-2 gap-4 pt-2">
        <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="overline text-gold">Company</div>
          <div className="font-display text-2xl font-bold text-white mt-1">{company}</div>
          <div className="text-sm text-white/60 mt-1">{tagline}</div>

          <div className="mt-6">
            <div className="overline mb-2 flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5 text-gold" /> Registered Office
            </div>
            <div className="text-white/85 leading-relaxed whitespace-pre-line" data-testid="contact-address">
              {address}
            </div>
          </div>
        </div>

        <div className="p-6 rounded-2xl border border-white/10 bg-white/[0.02] space-y-6">
          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <Globe className="w-3.5 h-3.5 text-gold" /> Contact
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-white/50" />
                <span data-testid="contact-website">{website}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-white/50" />
                <a
                  href={`mailto:${email}`}
                  className="text-gold hover:underline"
                  data-testid="contact-email"
                >
                  {email}
                </a>
              </div>
            </div>
          </div>

          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-gold" /> Support Hours
            </div>
            <div className="text-sm text-white/85 whitespace-pre-line" data-testid="contact-hours">{hours}</div>
          </div>

          <div>
            <div className="overline mb-2 flex items-center gap-2">
              <LifeBuoy className="w-3.5 h-3.5 text-gold" /> Response Time
            </div>
            <div className="text-sm text-white/85">{responseTime}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="overline mb-3">We can help with</div>
        <Bullets items={[
          "Order Status & Tracking",
          "Shipping & Delivery",
          "Personalized Certificates",
          "Moon & Mars Registry Verification",
          "Gift Package Assistance",
          "Refunds & Cancellations",
          "Payment Queries",
          "Technical Support",
          "General Inquiries",
        ]} />
      </div>

      <div className="pt-2">
        <a href={`mailto:${email}`} className="btn-primary" data-testid="contact-mail-btn">
          <Mail className="w-4 h-4" /> Email Support
        </a>
      </div>
    </PolicyLayout>
  );
}
